import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Wifi, TrendingUp, Calendar, Activity, Download, Bell, BarChart3, ClipboardList, Settings } from 'lucide-react';
import { authAPI, memberAPI, memberDemographicsAPI } from '../services/api';
import type { DashboardStats, PlatformStats, AdEvent, VpnStatus, MemberDemographics } from '../types';
import PlatformButton from '../components/PlatformButton';
import { PORTAL_SHOW_DATA_ACQUISITION } from '../config/portalUi';

export default function MemberDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats[]>([]);
  const [recentAds, setRecentAds] = useState<AdEvent[]>([]);
  const [categoryStats, setCategoryStats] = useState<{category: string; count: number}[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasConfig, setHasConfig] = useState(false);
  const [vpnStatus, setVpnStatus] = useState<VpnStatus | null>(null);
  const [memberCode, setMemberCode] = useState<string>('');
  const [hasApkUpdate, setHasApkUpdate] = useState(false);
  const [demographics, setDemographics] = useState<MemberDemographics | null>(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState({ ageRange: '', gender: '', dailySnsHours: '', primaryPlatform: '', prefecture: '' });
  const [surveySubmitting, setSurveySubmitting] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  // Extract IP identifier for display
  function getLastOctet(ip: string | null): string {
    if (!ip) return '?';
    const parts = ip.split('.').map(part => part.trim());
    if (parts.length === 4) {
      // For 10.77.x.x always show subnet.host to make the full /16 identifier visible
      if (parts[0] === '10' && parts[1] === '77') {
        const subnet = parts[2];
        const host = parts[3];
        return `${subnet}.${host}`;
      }
      // Legacy 10.13.13.x (shouldn't exist anymore)
      if (parts[0] === '10' && parts[1] === '13' && parts[2] === '13') {
        return parts[3];
      }
      return parts[3]; // Default to last octet
    }
    return '?';
  }

  async function loadDashboard() {
    try {
      const authCheck = await authAPI.checkMemberAuth();
      if (!authCheck.authenticated) {
        navigate('/');
        return;
      }

      // Store member code and VPN status
      if (authCheck.user) {
        setMemberCode(authCheck.user.code);
      }
      if (authCheck.vpn) {
        setVpnStatus(authCheck.vpn);
      }

      const hasRecentHandshake =
        authCheck.vpn?.handshakeAgeSec !== undefined &&
        authCheck.vpn?.handshakeAgeSec !== null;

      // When data-acquisition UI is hidden, skip the stats/platform/ad/category
      // round-trips entirely — the values are never rendered.
      const [statsData, platformData, adsData, catStats] = PORTAL_SHOW_DATA_ACQUISITION
        ? await Promise.all([
            memberAPI.getStats(),
            memberAPI.getPlatformStats(),
            memberAPI.getRecentAds(20),
            memberAPI.getCategoryStats().catch(() => [] as { category: string; count: number }[]),
          ])
        : [null, [] as PlatformStats[], [] as AdEvent[], [] as { category: string; count: number }[]];

      const [apkUpdate, demoData] = await Promise.all([
        memberAPI.hasApkUpdate().catch(() => ({ hasUpdate: false })),
        memberDemographicsAPI.get().catch(() => null),
      ]);
      setHasApkUpdate(apkUpdate.hasUpdate);
      setDemographics(demoData);

      setStats(statsData);
      setPlatformStats(platformData);
      setRecentAds(adsData);
      setCategoryStats(catStats);

      // Smart VPN detection: Show setup banner if user has no ad activity
      // This means they haven't set up or haven't used the VPN yet
      const hasAnyActivity = (statsData?.totalAds ?? 0) > 0 || platformData.length > 0;
      // Prefer handshake detection; fall back to activity if cache missing
      setHasConfig(hasRecentHandshake || hasAnyActivity);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      // Clear all state first
      setStats(null);
      setPlatformStats([]);
      setRecentAds([]);
      setHasConfig(false);

      // Logout from backend
      await authAPI.memberLogout();

      // Clear all browser storage
      sessionStorage.clear();
      localStorage.clear();

      // Force full page reload to clear any cached data
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, force reload to clear state
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = '/';
    }
  }

  function handlePlatformClick(platform: string) {
    const urls = {
      youtube: 'https://www.youtube.com',
      facebook: 'https://www.facebook.com',
      instagram: 'https://www.instagram.com',
      x: 'https://www.x.com',
    };
    window.open(urls[platform as keyof typeof urls], '_blank');
  }

  function getPlatformAdCount(platform: string): number {
    // Map frontend platform names to database platform names
    const dbPlatform = platform === 'x' ? 'x_twitter' : platform;
    const stat = platformStats.find(p => p.platform === dbPlatform);
    return stat?.count || 0;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800 truncate">ポータル</h1>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/portal/setup')}
              className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span className="hidden sm:inline">セットアップ</span>
            </button>
            <button
              onClick={() => navigate('/portal/app-download')}
              className={`relative flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-lg transition-colors ${
                hasApkUpdate
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-green-50 hover:bg-green-100 text-green-700'
              }`}
            >
              {hasApkUpdate ? (
                <>
                  <Bell className="w-5 h-5 animate-bounce" />
                  <span className="hidden sm:inline">更新あり</span>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span className="hidden sm:inline">アプリ</span>
                </>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">ログアウト</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* VPN Connection Status - Simplified */}
        {vpnStatus && memberCode && (
          <>
            {/* VPN Disconnected - Red */}
            {!vpnStatus.connected && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-red-500 rounded-full shadow-md animate-pulse" />
                  <p className="font-semibold text-red-800 text-lg">
                    {memberCode} ({getLastOctet(vpnStatus.expectedIp)})
                  </p>
                </div>
              </div>
            )}

            {/* VPN Connected but Wrong Profile - Orange */}
            {vpnStatus.connected && !vpnStatus.ipMatch && (
              <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-3 rounded-r-lg">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-orange-500 rounded-full shadow-md animate-pulse" />
                  <p className="font-semibold text-orange-800 text-lg">
                    {memberCode} ({getLastOctet(vpnStatus.expectedIp)})
                    <span className="text-orange-600"> ➜ 別のプロファイル ({getLastOctet(vpnStatus.currentIp)})</span>
                  </p>
                </div>
              </div>
            )}

            {/* VPN Connected and Correct Profile - Green */}
            {vpnStatus.connected && vpnStatus.ipMatch && (
              <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-3 rounded-r-lg">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-green-500 rounded-full shadow-md" />
                  <p className="font-semibold text-green-800 text-lg">
                    {memberCode} ({getLastOctet(vpnStatus.currentIp)})
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Setup Banner */}
        {!hasConfig && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Wifi className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-800">VPN設定が必要です</p>
                  <p className="text-sm text-yellow-700">広告トラッキングを開始するには、WireGuard VPNをセットアップしてください。</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/portal/setup')}
                className="w-full sm:w-auto px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors whitespace-nowrap"
              >
                セットアップへ
              </button>
            </div>
          </div>
        )}

        {/* Platform Buttons - Top Section - Same order as Flutter app */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">プラットフォーム</h2>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <PlatformButton
              platform="facebook"
              adCount={getPlatformAdCount('facebook')}
              onClick={() => handlePlatformClick('facebook')}
              showAdCount={PORTAL_SHOW_DATA_ACQUISITION}
            />
            <PlatformButton
              platform="instagram"
              adCount={getPlatformAdCount('instagram')}
              onClick={() => handlePlatformClick('instagram')}
              showAdCount={PORTAL_SHOW_DATA_ACQUISITION}
            />
            <PlatformButton
              platform="x"
              adCount={getPlatformAdCount('x')}
              onClick={() => handlePlatformClick('x')}
              showAdCount={PORTAL_SHOW_DATA_ACQUISITION}
            />
            <PlatformButton
              platform="youtube"
              adCount={getPlatformAdCount('youtube')}
              onClick={() => handlePlatformClick('youtube')}
              showAdCount={PORTAL_SHOW_DATA_ACQUISITION}
            />
          </div>
        </div>

        {/* Stats Cards - Below Platforms */}
        {PORTAL_SHOW_DATA_ACQUISITION && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-col items-center text-center">
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 mb-2" />
              <span className="text-xs sm:text-sm text-gray-600 mb-1">総広告数</span>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats?.totalAds || 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-col items-center text-center">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mb-2" />
              <span className="text-xs sm:text-sm text-gray-600 mb-1">今日</span>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats?.todayAds || 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-col items-center text-center">
              <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 mb-2" />
              <span className="text-xs sm:text-sm text-gray-600 mb-1">今週</span>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats?.weekAds || 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex flex-col items-center text-center">
              <Wifi className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 mb-2" />
              <span className="text-xs sm:text-sm text-gray-600 mb-1">プラットフォーム</span>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">{stats?.platformsUsed || 0}</p>
            </div>
          </div>
        </div>
        )}

        {/* Demographics Survey */}
        {!demographics?.surveyCompletedAt && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            {!showSurvey ? (
              <button onClick={() => setShowSurvey(true)}
                className="w-full flex items-center gap-3 text-left">
                <ClipboardList className="w-8 h-8 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800">{t('demographics.surveyTitle')}</p>
                  <p className="text-sm text-gray-500">{t('demographics.surveyDesc')}</p>
                </div>
              </button>
            ) : (
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-amber-500" /> {t('demographics.surveyTitle')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('demographics.ageRange')}</label>
                    <select value={surveyData.ageRange} onChange={e => setSurveyData(d => ({ ...d, ageRange: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="">--</option>
                      <option value="13-17">13-17</option>
                      <option value="18-24">18-24</option>
                      <option value="25-34">25-34</option>
                      <option value="35-44">35-44</option>
                      <option value="45+">45+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('demographics.gender')}</label>
                    <select value={surveyData.gender} onChange={e => setSurveyData(d => ({ ...d, gender: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="">--</option>
                      <option value="male">{t('demographics.male')}</option>
                      <option value="female">{t('demographics.female')}</option>
                      <option value="other">{t('demographics.other')}</option>
                      <option value="prefer_not_to_say">{t('demographics.preferNotToSay')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('demographics.dailySnsHours')}</label>
                    <select value={surveyData.dailySnsHours} onChange={e => setSurveyData(d => ({ ...d, dailySnsHours: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="">--</option>
                      <option value="<1">{t('demographics.lessThan1h')}</option>
                      <option value="1-2">{t('demographics.hours1to2')}</option>
                      <option value="2-4">{t('demographics.hours2to4')}</option>
                      <option value="4+">{t('demographics.moreThan4h')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('demographics.primaryPlatform')}</label>
                    <select value={surveyData.primaryPlatform} onChange={e => setSurveyData(d => ({ ...d, primaryPlatform: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm">
                      <option value="">--</option>
                      <option value="youtube">YouTube</option>
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="x">X (Twitter)</option>
                      <option value="tiktok">TikTok</option>
                      <option value="other">{t('demographics.other')}</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={async () => {
                    setSurveySubmitting(true);
                    try {
                      await memberDemographicsAPI.submit(surveyData);
                      setDemographics({ ...surveyData, surveyCompletedAt: new Date().toISOString() } as any);
                      setShowSurvey(false);
                    } catch (e) { console.error(e); }
                    setSurveySubmitting(false);
                  }}
                    disabled={surveySubmitting}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                    {surveySubmitting ? t('common.submitting') : t('demographics.submit')}
                  </button>
                  <button onClick={() => setShowSurvey(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm">
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category Distribution */}
        {PORTAL_SHOW_DATA_ACQUISITION && categoryStats.length > 0 && (() => {
          const categoryLabels: Record<string, string> = {
            other: 'その他',
            culture: '文化・エンタメ',
            economics: '経済・ショッピング',
            education: '教育',
            tourism: '観光・旅行',
            healthcare: '医療・健康',
            sports: 'スポーツ',
            politics: '政治',
            gambling_public: '公営ギャンブル',
            gambling_online: 'オンラインカジノ',
            gambling_pachinko: 'パチンコ',
            gambling_game: 'ガチャゲーム',
            gambling_gacha_premium: 'プレミアムガチャ',
            youtube: 'YouTube動画・チャンネル',
            unclassified: '未分類',
          };
          const categoryColors: Record<string, string> = {
            other: 'bg-gray-200 text-gray-700',
            culture: 'bg-purple-100 text-purple-700',
            economics: 'bg-yellow-100 text-yellow-700',
            education: 'bg-blue-100 text-blue-700',
            tourism: 'bg-cyan-100 text-cyan-700',
            healthcare: 'bg-pink-100 text-pink-700',
            sports: 'bg-green-100 text-green-700',
            politics: 'bg-indigo-100 text-indigo-700',
            gambling_public: 'bg-amber-100 text-amber-800',
            gambling_online: 'bg-red-100 text-red-700',
            gambling_pachinko: 'bg-orange-100 text-orange-700',
            gambling_game: 'bg-emerald-100 text-emerald-700',
            gambling_gacha_premium: 'bg-violet-100 text-violet-700',
            youtube: 'bg-rose-100 text-rose-700',
            unclassified: 'bg-slate-100 text-slate-500',
          };
          const barColors: Record<string, string> = {
            other: 'bg-gray-400',
            culture: 'bg-purple-500',
            economics: 'bg-yellow-500',
            education: 'bg-blue-500',
            tourism: 'bg-cyan-500',
            healthcare: 'bg-pink-500',
            sports: 'bg-green-500',
            politics: 'bg-indigo-500',
            gambling_public: 'bg-amber-500',
            gambling_online: 'bg-red-500',
            gambling_pachinko: 'bg-orange-500',
            gambling_game: 'bg-emerald-500',
            gambling_gacha_premium: 'bg-violet-500',
            youtube: 'bg-rose-500',
            unclassified: 'bg-slate-300',
          };
          const classified = categoryStats.filter(c => c.category !== 'unclassified');
          const total = classified.reduce((sum, c) => sum + Number(c.count), 0);
          const maxCount = classified.length > 0 ? Math.max(...classified.map(c => Number(c.count))) : 1;

          return (
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">広告カテゴリー分布</h2>
                <span className="text-sm text-gray-400 ml-auto">{total.toLocaleString()} 件分類済み</span>
              </div>
              <div className="space-y-2">
                {classified.slice(0, 8).map((cat) => {
                  const pct = total > 0 ? (Number(cat.count) / total * 100) : 0;
                  const barWidth = maxCount > 0 ? (Number(cat.count) / maxCount * 100) : 0;
                  return (
                    <div key={cat.category} className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap min-w-[120px] text-center ${categoryColors[cat.category] || 'bg-gray-100 text-gray-600'}`}>
                        {categoryLabels[cat.category] || cat.category}
                      </span>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${barColors[cat.category] || 'bg-gray-400'}`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 min-w-[60px] text-right">
                        {Number(cat.count).toLocaleString()} ({pct.toFixed(1)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Recent Activity - Enriched */}
        {PORTAL_SHOW_DATA_ACQUISITION && (
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">最近の広告活動</h2>
          {recentAds.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">まだ広告が記録されていません</p>
              <p className="text-sm text-gray-400">VPNに接続してブラウジングを開始してください</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentAds.map((ad) => {
                const platformColors: Record<string, string> = {
                  youtube: 'border-red-500',
                  facebook: 'border-blue-600',
                  instagram: 'border-pink-500',
                  x_twitter: 'border-gray-800',
                  generic: 'border-gray-400',
                  tiktok: 'border-teal-500',
                };
                const platformBgColors: Record<string, string> = {
                  youtube: 'bg-red-50',
                  facebook: 'bg-blue-50',
                  instagram: 'bg-pink-50',
                  x_twitter: 'bg-gray-50',
                  generic: 'bg-gray-50',
                  tiktok: 'bg-teal-50',
                };
                const platformLabels: Record<string, string> = {
                  youtube: 'YouTube',
                  facebook: 'Facebook',
                  instagram: 'Instagram',
                  x_twitter: 'X',
                  generic: 'Web',
                  tiktok: 'TikTok',
                };
                const categoryLabels: Record<string, string> = {
                  other: 'その他',
                  culture: '文化',
                  economics: '経済',
                  education: '教育',
                  tourism: '旅行',
                  healthcare: '医療',
                  sports: 'スポーツ',
                  politics: '政治',
                  gambling_public: '公営ギャンブル',
                  gambling_online: 'オンラインカジノ',
                  gambling_pachinko: 'パチンコ',
                  gambling_game: 'ゲーム',
                  gambling_gacha_premium: 'ガチャ',
                };
                const categoryBadgeColors: Record<string, string> = {
                  other: 'bg-gray-100 text-gray-600',
                  culture: 'bg-purple-100 text-purple-700',
                  economics: 'bg-yellow-100 text-yellow-700',
                  education: 'bg-blue-100 text-blue-700',
                  tourism: 'bg-cyan-100 text-cyan-700',
                  healthcare: 'bg-pink-100 text-pink-700',
                  sports: 'bg-green-100 text-green-700',
                  politics: 'bg-indigo-100 text-indigo-700',
                  gambling_public: 'bg-amber-100 text-amber-800',
                  gambling_online: 'bg-red-200 text-red-800',
                  gambling_pachinko: 'bg-orange-100 text-orange-700',
                  gambling_game: 'bg-emerald-100 text-emerald-700',
                  gambling_gacha_premium: 'bg-violet-100 text-violet-700',
                };

                const borderColor = platformColors[ad.platform] || 'border-blue-500';
                const bgColor = platformBgColors[ad.platform] || 'bg-gray-50';
                const platformLabel = platformLabels[ad.platform] || ad.platform;

                // Use enriched data with fallbacks
                // Note: API returns snake_case from PostgreSQL
                const adAny = ad as any;
                const advertiser = adAny.advertiser_name
                  || adAny.advertiser_domain
                  || adAny.host
                  || null;

                const title = adAny.ad_title
                  || adAny.page_title
                  || null;

                const description = adAny.ad_description || null;
                const thumbnail = adAny.thumbnail_url || null;

                const category = adAny.category;
                const categoryLabel = category ? (categoryLabels[category] || category) : null;
                const categoryBadge = category ? (categoryBadgeColors[category] || 'bg-gray-100 text-gray-600') : null;

                // Build clickable URL from best available source
                const clickUrl = adAny.click_url
                  || adAny.advertiser_url
                  || (adAny.ad_video_id ? `https://www.youtube.com/watch?v=${adAny.ad_video_id}` : null)
                  || (adAny.platform === 'youtube' && adAny.content_id ? `https://www.youtube.com/watch?v=${adAny.content_id}` : null)
                  || (adAny.advertiser_domain ? `https://${adAny.advertiser_domain}` : null)
                  || null;

                // For YouTube ads with video_id, build a proper YouTube thumbnail
                const youtubeThumb = !thumbnail && adAny.ad_video_id
                  ? `https://i.ytimg.com/vi/${adAny.ad_video_id}/mqdefault.jpg`
                  : null;
                const displayThumb = thumbnail || youtubeThumb;

                const cardContent = (
                  <div className="flex gap-3">
                    {/* Thumbnail */}
                    {displayThumb && (
                      <div className="flex-shrink-0">
                        <img
                          src={displayThumb}
                          alt=""
                          className="w-20 h-14 sm:w-24 sm:h-16 object-cover rounded-lg bg-gray-200"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Top row: Platform + Category badge + Timestamp */}
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-semibold text-gray-500 uppercase">{platformLabel}</span>
                          {categoryLabel && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${categoryBadge}`}>
                              {categoryLabel}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(ad.timestamp).toLocaleString('ja-JP', {
                            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>

                      {/* Advertiser name (bold) */}
                      {advertiser && (
                        <p className="font-semibold text-gray-800 text-sm truncate" title={advertiser}>
                          {advertiser}
                        </p>
                      )}

                      {/* Ad title */}
                      {title && title !== advertiser && (
                        <p className="text-sm text-gray-600 line-clamp-1" title={title}>
                          {title}
                        </p>
                      )}

                      {/* Description fallback when no title */}
                      {!title && description && (
                        <p className="text-sm text-gray-500 line-clamp-1" title={description}>
                          {description}
                        </p>
                      )}

                      {/* Domain + open link hint */}
                      <div className="flex items-center gap-2 mt-0.5">
                        {adAny.advertiser_domain && adAny.advertiser_domain !== advertiser && (
                          <span className="text-xs text-gray-400">{adAny.advertiser_domain}</span>
                        )}
                        {clickUrl && (
                          <span className="text-xs text-blue-400">
                            {adAny.platform === 'youtube' ? '▶ YouTubeで開く' : '↗ 開く'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );

                return clickUrl ? (
                  <a
                    key={ad.id}
                    href={clickUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block border-l-4 ${borderColor} ${bgColor} p-3 sm:p-4 rounded-r-lg hover:shadow-md transition-shadow cursor-pointer`}
                  >
                    {cardContent}
                  </a>
                ) : (
                  <div key={ad.id} className={`border-l-4 ${borderColor} ${bgColor} p-3 sm:p-4 rounded-r-lg`}>
                    {cardContent}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
